import { forwardRef, ReactNode } from 'react';

import { Field, FieldProps } from 'react-final-form';

import {
  ChipInputWithDropdown,
  ChipInputWithDropdownProps,
} from '@cast/design-system';

import { useControlsGroupState } from './controls-state';
import { useRffFormState } from '../hooks';
import { showErrorOnChange } from '../utils';

type RffChipInputWithDropdownProps = Omit<
  ChipInputWithDropdownProps,
  'name' | 'value' | 'onChange'
> & {
  name: string;
  renderError?: (error: string | string[]) => ReactNode;
  fieldProps?: Partial<FieldProps<string[], any>>;
};

export const RffChipInputWithDropdown = forwardRef<
  HTMLElement,
  RffChipInputWithDropdownProps
>(({ fieldProps = {}, renderError, ...chipInputProps }, ref) => {
  const { mode } = useRffFormState();
  const { disabled, readOnly, name } = useControlsGroupState({
    disabled: chipInputProps.disabled,
    readOnly: mode === 'view' || chipInputProps.readOnly,
    name: chipInputProps.name,
  });
  return (
    <Field
      {...fieldProps}
      name={name || chipInputProps.name}
      render={(props) => {
        const {
          input: { onBlur, onFocus, onChange, value },
          meta,
        } = props;
        const showError = showErrorOnChange(meta);
        return (
          <ChipInputWithDropdown
            onBlur={onBlur}
            onFocus={onFocus}
            error={
              showError ? renderError?.(meta.error) || meta.error : undefined
            }
            success={meta.touched && meta.valid}
            disabled={disabled}
            readOnly={readOnly}
            {...chipInputProps}
            onChange={onChange}
            value={value}
            ref={ref}
          />
        );
      }}
    />
  );
});

RffChipInputWithDropdown.displayName = 'RffChipInputWithDropdown';
